// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import PaginationPresentationPropsDefault from "@insite/mobius/Pagination/presentationProps";
import { getProp } from "@insite/mobius/utilities";
import ChevronLeft from "@west/XDL/Icons/ChevronLeft";
import ChevronRight from "@west/XDL/Icons/ChevronRight";
import { buttonTransparent } from "@west/XDL/PreStyle/button";
import { xBaseTheme } from "@west/XDL/types";
import { color, xScreen } from "@west/XDL/Utilities";
import { css } from "styled-components";

const selectPropsStyles = {
    label: {
        width: 74,
    },
    input: {
        width: 68,
        paddingRight: 15,
    },
};

export default {
    defaultProps: {
        ...PaginationPresentationPropsDefault,
        mergeCss: true,
        selectProps: {
            mergeCss: true,
            labelProps: {
                mergeCss: true,
                size: 12,
                weight: "normal",
                css: css`
                    letter-spacing: 0;
                    line-height: 14px;
                    min-height: initial;
                `,
            },
            cssOverrides: {
                formField: css`
                    width: ${selectPropsStyles.input.width +
                    selectPropsStyles.input.paddingRight +
                    selectPropsStyles.label.width}px;
                    padding-right: 15px;
                    min-width: 117px;
                `,
                formInputWrapper: css`
                    width: calc(100% - ${selectPropsStyles.label.width}px);
                    max-width: ${selectPropsStyles.input.width}px;
                    ${IconWrapper} > svg {
                        height: 12px;
                        width: 12px;
                    }
                `,
            },
            css: css`
                &&&&&& {
                    padding-right: 24px;
                }
            `,
        },
        paginationContainer: {
            css: css`
                &&& {
                    display: inline-flex;
                    align-items: baseline;
                    gap: 12px;
                    margin: auto;
                }
            `,
        },
        cssOverrides: {
            pagination: css`
                ${xScreen("lte", "md")} {
                    width: 100%;
                    justify-content: space-between;
                }
            `,
            linkList: css`
                ul {
                    display: flex;
                    gap: 5px;
                    align-items: center;
                }
                ${xScreen("lte", "md")} {
                    margin-left: auto;
                }

                ${xScreen("lte", "md")} {
                    width: auto;
                }
            `,
            perPageSelect: css`
                ${xScreen("lte", "md")} {
                    width: auto;
                }
            `,
        },
        paginationSelect: {
            sizeVariant: "small",
            backgroundColor: "transparent",
            cssOverrides: {
                formField: css`
                    width: auto;
                    min-width: 35px;

                    & select:not(.react-datetime-picker__inputGroup__input):not([name="datetime"]) {
                        &,
                        &:not([class^="PopoverTriggerInput"]):hover:not(:disabled),
                        &:not([class^="PopoverTriggerInput"]):focus:not(:disabled) {
                            background: transparent;
                            border: none;
                            box-shadow: none;
                            font-size: 16px;
                            font-weight: normal;
                            letter-spacing: 0;
                            line-height: 18px;
                            height: 18px;
                            cursor: pointer;

                            & ~ ${IconWrapper} {
                                padding: 0;
                                top: 0;
                                &,
                                & > svg {
                                    width: 11px;
                                    height: 18px;
                                }
                            }
                        }

                        &:not(:disabled):hover ~ ${IconWrapper}, &[aria-expanded="true"] ~ ${IconWrapper} {
                            color: ${color("common.ui9")};
                        }
                    }

                    &&&& select {
                        padding-left: 0;
                        padding-right: 0;
                    }
                `,
            },
        },
        buttonProps: {
            css: css`
                ${buttonTransparent}

                &&&& {
                    width: 32px;
                }

                &[data-test-selector="pagination_previousButton"] {
                    margin: 0 12px 0 0;
                    ${IconWrapper}, ${IconWrapper} > svg {
                        width: 32px;
                        height: 32px;
                    }
                }
                &[data-test-selector="pagination_nextButton"] {
                    margin: 0 0 0 12px;
                    ${IconWrapper}, ${IconWrapper} > svg {
                        width: 32px;
                        height: 32px;
                    }
                }
                &:not(:disabled) > ${IconWrapper} {
                    color: ${color("common.ui6")};
                }
                &:not(:disabled):hover > ${IconWrapper} {
                    color: ${color("common.ui9")};
                }
                &:not(:disabled):focus > ${IconWrapper} {
                    color: ${color("common.ui10")};
                }
                &:disabled > ${IconWrapper} {
                    color: ${color("common.ui6")};
                    opacity: 0.33;
                }

                &,
                & > ${IconWrapper} {
                    transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
                }
            `,
        },
    },
    navIconsSrc: {
        firstPage: "ChevronsLeft",
        previousPage: ChevronLeft,
        nextPage: ChevronRight,
        lastPage: "ChevronsRight",
    },
} as xBaseTheme["pagination"];
